import React from 'react';
import clsx from 'clsx';
import {type UseFormReturn} from 'react-hook-form';
import {SetSelector} from '@netx/core-attribute-set-selection/components/SetSelector';
import {
	AttributeSetsContainer,
	AttributeSetsContainerProps,
} from '@netx/core-attribute-set/components/Sets/AttributeSets';
import {Alert} from '@netx/core-components/Alert';
import {Form} from '@netx/core-components/Form';
import {
	ScrollBorder,
	scrollBorderClasses,
} from '@netx/core-components/ScrollBorder';
import SubmitButton from '@netx/core-components/SubmitButton';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {
	ASSET_ATTRIBUTE_EDITOR_CLASS_NAME,
	assetAttributeEditorClasses,
	type AssetAttributeEditorClasses,
	getAssetAttributeEditorUtilityClass,
} from './assetAttributeEditorClasses';

export * from './assetAttributeEditorClasses';

export type AssetAttributeEditorOwnerState = WithClasses<
	AssetAttributeEditorClasses,
	{
		disabled?: boolean;
	}
>;

const useUtilityClasses = (ownerState: AssetAttributeEditorOwnerState) => {
	const {classes, disabled} = ownerState;
	const slots = {
		root: ['root', disabled && 'disabled'],
		attributesWrapper: ['attributesWrapper'],
		form: ['form'],
		footer: ['footer'],
		selector: ['selector'],
	};
	return composeClasses(slots, getAssetAttributeEditorUtilityClass, classes);
};

const ScrollBorderRoot = styled(ScrollBorder)(({theme}) => ({
	[`& .${scrollBorderClasses.scrollWrapper}`]: {
		flex: '1 1 auto',
		padding: theme.spacing(0, 2),
	},
	[`& .${scrollBorderClasses.scrollWrapperInner}`]: {
		flex: '1 0 auto',
	},
}));

const AssetAttributeEditorRoot = styled('div', {
	name: ASSET_ATTRIBUTE_EDITOR_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	...theme.mixins.flexFillColumn,
	position: 'relative',
	width: '100%',
	[`&.${assetAttributeEditorClasses.disabled}`]: {
		pointerEvents: 'none',
		[`& .${assetAttributeEditorClasses.attributesWrapper}`]: {
			...theme.mixins.disabled,
		},
	},
	'& .url-link': {
		color: theme.palette.primary.main,
		textDecoration: 'underline',
	},
}));

const AssetAttributeEditorAttributesWrapper = styled('div', {
	name: ASSET_ATTRIBUTE_EDITOR_CLASS_NAME,
	slot: 'AttributesWrapper',
})(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
	flexDirection: 'column',
}));

const AssetAttributeEditorForm = styled(Form, {
	name: ASSET_ATTRIBUTE_EDITOR_CLASS_NAME,
	slot: 'Form',
})(({theme}) => ({
	...theme.mixins.flexFillColumn,
	position: 'relative',
	width: '100%',
}));

const AssetAttributeEditorFooter = styled('footer', {
	name: ASSET_ATTRIBUTE_EDITOR_CLASS_NAME,
	slot: 'Footer',
})(({theme}) => ({
	...theme.mixins.formFooter,
	position: 'relative',
}));

const AssetAttributeEditorSetSelector = styled(SetSelector, {
	name: ASSET_ATTRIBUTE_EDITOR_CLASS_NAME,
	slot: 'Selector',
})(({theme}) => ({
	flex: '0 0 auto',
}));

export interface AssetAttributeEditorProps
	extends
		Omit<AttributeSetsContainerProps, 'classes' | 'control'>,
		WithClasses<AssetAttributeEditorClasses> {
	form: UseFormReturn;
	hideSetSelector?: boolean;
	disabled?: boolean;
	isLoading?: boolean;
	progress?: number | null;
	readOnly?: boolean;
	submitLabel?: string;
	working?: boolean;
	onSubmit?: (event: React.FormEvent) => void;
}

export const AssetAttributeEditorPrimitive = ({
	classes: _classes,
	className,
	disabled: _disabled,
	error,
	form,
	hideSetSelector,
	isLoading,
	progress,
	readOnly,
	submitLabel,
	working,
	onSubmit,
	...props
}: AssetAttributeEditorProps) => {
	const disabled = _disabled || isLoading || working;

	const ownerState: AssetAttributeEditorOwnerState = {
		classes: _classes,
		className,
		disabled,
	};

	const classes = useUtilityClasses(ownerState);

	const dirty = form.formState.isDirty;
	const isValid = form.formState.isValid;
	const submitEnabled = !disabled && dirty && isValid;

	return (
		<AssetAttributeEditorRoot className={clsx(classes.root, className)}>
			{error ? (
				<Alert
					severity="error"
					sx={{textWrap: 'auto', ml: 2, mr: 2, mt: 0, mb: 1}}
				>
					{error}
				</Alert>
			) : null}
			{!hideSetSelector ? (
				<AssetAttributeEditorSetSelector className={classes.selector} />
			) : null}
			<AssetAttributeEditorForm onSubmit={onSubmit} className={classes.form}>
				<ScrollBorderRoot>
					<AssetAttributeEditorAttributesWrapper
						className={classes.attributesWrapper}
					>
						<AttributeSetsContainer
							enforceMandatory={false}
							readOnly={readOnly}
							{...props}
							control={form.control}
						/>
					</AssetAttributeEditorAttributesWrapper>
				</ScrollBorderRoot>
				{(!readOnly && (
					<AssetAttributeEditorFooter className={classes.footer}>
						<SubmitButton
							disabled={!submitEnabled}
							fullWidth
							label={submitLabel}
							showWorking={isLoading || working}
							progress={progress}
						/>
					</AssetAttributeEditorFooter>
				)) ||
					null}
			</AssetAttributeEditorForm>
		</AssetAttributeEditorRoot>
	);
};

export const AssetAttributeEditor = AssetAttributeEditorPrimitive;

export default AssetAttributeEditor;
